import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import {
    Box,
    Button,
    CircularProgress,
    InputAdornment,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { v4 as uuidv4 } from 'uuid';
import { resizeFile } from '../../../helpers/helpers';
import sanity from '../../../sanity';
import { getSymbolForCurrency, transformToSanityUrl } from '../../../utils';
import colors from '../../../utils/colors';
import styles from './editVideo.module.scss';
import { getBase64 } from '../../../helpers/FileReader';
import fetch from '../../../fetch';
import CircularProgressWithLabel from '../../CreateVideo/CircularProgressWithLabel';
import { fromUnitAmount } from '../../../utils/unitAmountHelper';
import { CONVERT_FINISHED_STATUS, CONVERT_STATUS } from '../../../constants';
import EditVideoContent from './components/EditVideoContent';
import NavMobile from '../../Nav/NavMobile';
import NavDesktop from '../../Nav/NavDesktop';

const mapStateToProps = (state) => {
    return {
        company: state.common.company,
        upload: state.common.upload,
        connected_account: state.common.connected_account,
        events: state.event.events,
        currency: state.common.currency
    };
};

// const mapDispatchToProps = (dispatch) => ({
//     onLoad: (payload) => dispatch({ type: EVENT_LIST_PAGE_LOADED, payload }),
//     onPriceChange: (payload) => dispatch({ type: CONNECT_PRICE_CHANGE, payload })
// });

const EditVideo = (props) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { id } = useParams();
    const [event, setEvent] = useState();
    const [loading, setLoading] = useState(false);
    const [thumbnail, setThumbnail] = useState(null);
    const uniqueHash = useMemo(() => uuidv4(), []);
    // const [viewLandscape, setViewLandscape] = useState(false);

    const [imagePreview, setImagePreview] = useState(null);
    const [title, setTitle] = useState('');
    const [price, setPrice] = useState('');
    const [priceObject, setPriceObject] = useState();
    const [description, setDescription] = useState('');
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [isEditingPrice, setIsEditingPrice] = useState(false);
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [changedThumbnail, setChangedThumbnail] = useState(null);
    const [upload, setUpload] = useState();
    const [jobId, setJobId] = useState(uuidv4());
    const [product, setProduct] = useState();
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [priceText, setPriceText] = useState();
    const [showLoader, setShowLoader] = useState(false);
    const [trailerUpdated, setTrailerUpdated] = useState(false);

    const fetchEvent = async () => {
        try {
            setLoading(true);
            const event = await sanity.Events.GetEventById(id);
            const product = await fetch.Products.Get(undefined, event.pay_per_view_product);
            setPriceObject(product.default_price);
            const price = fromUnitAmount(product.default_price.unit_amount_decimal);
            setPrice(fromUnitAmount(product.default_price.unit_amount_decimal));
            if (props.currency.alignment === 'right') {
                setPriceText(`${price} ${product.default_price.currency.toUpperCase()}`);
            } else {
                setPriceText(`${product.default_price.currency.toUpperCase()} ${price}`);
            }
            setProduct(product);
            setEvent(event);
        } catch (error) {
            console.log('ERROR:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchEvent();
        }
    }, []);

    useEffect(() => {
        if (trailerUpdated) {
            setShowLoader(true);
            if (props.events.length > 0) {
                const event = props.events.filter((e) => e.id === id);
                if (event) {
                    if (event[0].trailerProcessingStatus === CONVERT_FINISHED_STATUS) {
                        setShowLoader(false);
                        setTrailerUpdated(false);
                        fetchEvent();
                    }
                }
            }
        }
    }, [props.events, trailerUpdated]);

    const handleImageChange = async (e) => {
        e.preventDefault();
        if (!e.target.files[0]) {
            return;
        }
        let reader = new FileReader();
        let currentFile = e.target.files[0];
        const allowedExtensions = ['jpg', 'jpeg', 'png'];
        const fileExtension = currentFile.name.split('.').pop().toLowerCase();

        if (!allowedExtensions.includes(fileExtension)) {
            // presentToast(
            //     'Please select a valid image file (jpg, jpeg, png).',
            //     closeCircleSharp,
            //     'danger',
            //     1500
            // );
            toast.error(t('PleaseSelectValidImage'));
            return;
        }

        reader.onloadend = () => {
            setImagePreview(reader.result);
        };

        try {
            if (currentFile) {
                const image = await resizeFile(currentFile);
                setChangedThumbnail(image);
                reader.readAsDataURL(image);
            }
        } catch (err) {
            console.error('ERROR with thumbnail: ', err);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            if (event) {
                try {
                    const thumbnail = await event.getThumbnail(1200, 800);
                    setTitle(event.title);
                    setDescription(event.description);
                    const image = sanity.urlFor(event.thumbnail.image_asset.asset);
                    const transformedImage = transformToSanityUrl(image.options);
                    setThumbnail(transformedImage);
                } catch (error) {
                    console.log('ERROR: ', error);
                }
            }
        };
        fetchData();
    }, [event]);

    useEffect(() => {
        if (event) {
            if (props.currency.alignment === 'right') {
                setPriceText(`${price} ${product?.default_price?.currency.toUpperCase()}`);
            } else {
                setPriceText(`${product?.default_price?.currency.toUpperCase()} ${price}`);
            }
        }
    }, [price]);

    useEffect(() => {
        if (jobId && props.upload) {
            const upload = props.upload.find((x) => x.jobId === jobId);
            setUpload(upload);
        }
    }, [props.upload, jobId]);

    const handleSave = async () => {
        try {
            setSaveLoading(true);
            let _thumbnail = null;
            let transformedImage = null;
            if (changedThumbnail) {
                const base64Image = await getBase64(changedThumbnail);
                const data = base64Image.split(';base64,')[1];
                const requestOptions = {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        img: {
                            data,
                            name: new Date().getTime()
                        }
                    })
                };
                const res = await fetch.Event.UploadThumbnail(requestOptions);
                const image = sanity.urlFor(res.image_asset.asset);
                transformedImage = transformToSanityUrl(image.options);

                _thumbnail = { ..._thumbnail, _ref: res._id };
            }
            // if (_thumbnail) {
            //     let newPrice = event.pay_per_view_price;
            //     if (price !== event.pay_per_view_price_amount) {
            //         const priceObject = await fetch.Prices.Create(
            //             undefined,
            //             props.connected_account.accountId,
            //             price,
            //             event.pay_per_view_price_currency,
            //             product.id
            //         );
            //         newPrice = priceObject.id;
            //     }
            //     await fetch.Event.UpdateEvent({
            //         method: 'PATCH',
            //         body: JSON.stringify({
            //             event: {
            //                 title: title,
            //                 description: description,
            //                 pay_per_view_price: newPrice,
            //                 pay_per_view_price_amount: price,
            //                 _id: event.id,
            //                 thumbnail: _thumbnail,
            //                 trailer: upload?.trailer_url ?? event.trailer
            //             }
            //         })
            //     });
            // } else {
            if (title !== event.title || description !== event.description || changedThumbnail) {
                const data = {
                    name: title,
                    description: description,
                    // caption: description,
                    images: transformedImage ? [transformedImage] : product.images
                };
                const newProduct = await fetch.Products.Update(product.id, undefined, data);
            }
            let newPrice;
            if (price !== fromUnitAmount(priceObject.unit_amount_decimal)) {
                newPrice = await fetch.Prices.Create(
                    undefined,
                    undefined,
                    price,
                    priceObject.currency,
                    product.id
                );
            }
            await fetch.Event.UpdateEvent({
                method: 'PATCH',
                body: JSON.stringify({
                    event: _thumbnail
                        ? {
                              title: title,
                              description: description,
                              _id: event.id,
                              trailer: upload?.trailer_url ?? event.trailer,
                              trailerProcessingStatus: CONVERT_STATUS,
                              thumbnail: _thumbnail
                          }
                        : {
                              title: title,
                              description: description,
                              _id: event.id,
                              trailer: upload?.trailer_url ?? event.trailer,
                              trailerProcessingStatus: CONVERT_STATUS
                          }
                })
            });
            if (upload?.trailer_url) {
                setTrailerUpdated(true);
            }
            // }
            toast.success('Event updated');
        } catch (error) {
            console.log('ERROR: ', error);
        } finally {
            setSaveLoading(false);
        }
    };

    const deleteEvent = async () => {
        try {
            setDeleteLoading(true);
            await fetch.Event.DeleteEvent(event.id);
        } catch (error) {
            console.log('ERROR: ', error);
        } finally {
            setDeleteLoading(false);
            history.goBack();
        }
    };

    return (
        <Box>
            {isMobile ? <NavMobile /> : <NavDesktop />}
            <Box sx={{ padding: isMobile ? '1rem 1rem 0 1rem' : '1rem 3rem 0rem' }}>
                <Box className={styles.main}>
                    {loading ? (
                        <Box className={styles.flexProgress}>
                            <CircularProgress sx={{ color: colors.primary }} />
                        </Box>
                    ) : (
                        <>
                            {event && (
                                <>
                                    {!isMobile && (
                                        <Box className={styles.buttons}>
                                            <Button
                                                onClick={() => history.goBack()}
                                                sx={{
                                                    '&:active, &:focus, &:hover': {
                                                        backgroundColor: '#252525',
                                                        color: 'white'
                                                    }
                                                }}>
                                                <KeyboardBackspaceIcon />
                                                {t('Back')}
                                            </Button>
                                            {saveLoading ? (
                                                <Box>
                                                    <CircularProgress
                                                        sx={{ color: colors.primary }}
                                                    />
                                                </Box>
                                            ) : (
                                                <>
                                                    {upload?.trailer_show ? (
                                                        <Box>
                                                            <CircularProgressWithLabel
                                                                color={colors.primary}
                                                                showValue={false}
                                                                value={upload.trailer_progress}
                                                            />
                                                        </Box>
                                                    ) : (
                                                        <Button
                                                            onClick={() => handleSave()}
                                                            sx={{
                                                                '&:active, &:focus, &:hover': {
                                                                    backgroundColor: '#252525',
                                                                    color: 'white'
                                                                }
                                                            }}>
                                                            {t('Save')}
                                                        </Button>
                                                    )}
                                                </>
                                            )}
                                        </Box>
                                    )}
                                    <Box className={styles.content}>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            className={styles.centerInput}
                                            onBlur={() => {
                                                setIsEditingTitle(false);
                                            }}>
                                            {isEditingTitle ? (
                                                <TextField
                                                    multiline
                                                    autoFocus
                                                    spellCheck={false}
                                                    value={title}
                                                    onChange={(e) => {
                                                        setTitle(e.target.value);
                                                    }}
                                                    variant="standard"
                                                    InputProps={{
                                                        disableUnderline: true,
                                                        sx: {
                                                            padding: '0 !important',
                                                            fontSize: isMobile
                                                                ? '32px !important'
                                                                : '64px !important',
                                                            color: 'white',
                                                            lineHeight: isMobile ? '34px' : '74px',
                                                            letterSpacing: isMobile
                                                                ? '-0.64px'
                                                                : '-1.28px',
                                                            textAlign: 'center'
                                                        }
                                                    }}
                                                    inputProps={{
                                                        style: {
                                                            textAlign: 'center',
                                                            whiteSpace: 'pre-wrap',
                                                            wordWrap: 'break-word'
                                                        }
                                                    }}
                                                    sx={{
                                                        '& .MuiInputBase-root': {
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            height: '100%',
                                                            textAlign: 'center'
                                                        },
                                                        width: '100%',
                                                        textAlign: 'center',
                                                        lineHeight: isMobile ? '34px' : '74px'
                                                    }}
                                                />
                                            ) : (
                                                <Box
                                                    onClick={() => setIsEditingTitle(true)}
                                                    sx={{
                                                        padding: '0 !important',
                                                        textAlign: 'center',
                                                        fontSize: isMobile
                                                            ? '32px !important'
                                                            : '64px !important',
                                                        lineHeight: isMobile ? '34px' : '74px',
                                                        letterSpacing: isMobile
                                                            ? '-0.64px'
                                                            : '-1.28px',
                                                        whiteSpace: 'pre-wrap'
                                                    }}>
                                                    {title}
                                                </Box>
                                            )}
                                        </Box>
                                        <Box className={styles.buyButton}>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent={'center'}
                                                onBlur={() => {
                                                    setIsEditingPrice(false);
                                                }}>
                                                {isEditingPrice ? (
                                                    <TextField
                                                        spellCheck={false}
                                                        value={price}
                                                        onChange={(e) => setPrice(e.target.value)}
                                                        autoFocus
                                                        variant="standard"
                                                        InputProps={{
                                                            disableUnderline: true,
                                                            sx: {
                                                                textAlign: 'left',
                                                                color: 'white',
                                                                width: '100%',
                                                                backgroundColor: '#3f3f3f',
                                                                borderRadius: '9px'
                                                                // padding: '0.5rem'
                                                            }
                                                        }}
                                                    />
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            marginBottom: '0 !important',
                                                            padding: '0.5rem'
                                                        }}
                                                        onClick={() => setIsEditingPrice(true)}>
                                                        {t('Buy')} {priceText}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                        <Typography
                                            sx={{
                                                opacity: 0.6,
                                                margin: '1rem 0',
                                                lineHeight: 'normal',
                                                letterSpacing: '-0.14px'
                                            }}
                                            fontSize={'14px'}
                                            textAlign={'center'}>
                                            {t('MinimumPrice', {
                                                minimumPrice: `${
                                                    event.minimum_calculated_price
                                                } ${priceObject?.currency.toUpperCase()}`
                                            })}
                                        </Typography>
                                        {!isMobile && (
                                            <Box
                                                sx={{ margin: '2rem 0' }}
                                                onBlur={() => {
                                                    setIsEditingDescription(false);
                                                }}>
                                                {isEditingDescription ? (
                                                    <TextField
                                                        fullWidth
                                                        autoFocus
                                                        spellCheck={false}
                                                        value={description}
                                                        onChange={(e) => {
                                                            setDescription(e.target.value);
                                                        }}
                                                        multiline
                                                        variant="standard"
                                                        InputProps={{
                                                            disableUnderline: true,
                                                            sx: {
                                                                padding: '0 !important',
                                                                opacity: '0.6 !important',
                                                                color: 'white',
                                                                fontSize: '20px',
                                                                lineHeight: 'normal',
                                                                letterSpacing: isMobile
                                                                    ? '-0.16px'
                                                                    : '-0.2px'
                                                            }
                                                        }}
                                                    />
                                                ) : (
                                                    <Box
                                                        onClick={() =>
                                                            setIsEditingDescription(true)
                                                        }
                                                        sx={{
                                                            padding: '0 !important',
                                                            opacity: 0.6,
                                                            fontSize: '20px',
                                                            lineHeight: 'normal',
                                                            letterSpacing: isMobile
                                                                ? '-0.16px'
                                                                : '-0.2px'
                                                        }}>
                                                        {description}
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                        <EditVideoContent
                                            event={event}
                                            uniqueHash={uniqueHash}
                                            imagePreview={imagePreview}
                                            setImagePreview={setImagePreview}
                                            thumbnail={thumbnail}
                                            handleImageChange={handleImageChange}
                                            changedThumbnail={changedThumbnail}
                                            jobId={jobId}
                                            showLoader={showLoader}
                                        />
                                        {isMobile && (
                                            <Box
                                                sx={{
                                                    margin: '2rem 0',
                                                    padding: '0 0.5rem'
                                                }}
                                                onBlur={() => {
                                                    setIsEditingDescription(false);
                                                }}>
                                                {isEditingDescription ? (
                                                    <TextField
                                                        multiline
                                                        fullWidth
                                                        autoFocus
                                                        spellCheck={false}
                                                        value={description}
                                                        onChange={(e) => {
                                                            setDescription(e.target.value);
                                                        }}
                                                        variant="standard"
                                                        InputProps={{
                                                            disableUnderline: true,
                                                            sx: {
                                                                padding: '0 !important',
                                                                fontSize: '16px',
                                                                opacity: '0.6 !important',
                                                                color: 'white',
                                                                lineHeight: 'normal',
                                                                letterSpacing: '-0.16px'
                                                            }
                                                        }}
                                                    />
                                                ) : (
                                                    <Box
                                                        onClick={() =>
                                                            setIsEditingDescription(true)
                                                        }
                                                        sx={{
                                                            padding: '0 !important',
                                                            opacity: 0.6,
                                                            fontSize: '16px',
                                                            lineHeight: 'normal',
                                                            letterSpacing: '-0.16px'
                                                        }}>
                                                        {description}
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                </>
                            )}
                            <Box className={styles.creator}>
                                <Typography color={'white'}>{t('Creator')}</Typography>
                                <Button
                                    sx={{
                                        '&:active, &:focus, &:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                            color: 'white'
                                        }
                                    }}>
                                    {props?.company?.name}
                                </Button>
                            </Box>
                            <Box className={styles.deleteButton}>
                                {deleteLoading ? (
                                    <CircularProgress sx={{ color: colors.primary }} />
                                ) : (
                                    <Button onClick={() => deleteEvent()}>{t('Delete')}</Button>
                                )}
                            </Box>
                            {isMobile && (
                                <Box className={styles.saveButton}>
                                    {saveLoading ? (
                                        <Box>
                                            <CircularProgress sx={{ color: colors.primary }} />
                                        </Box>
                                    ) : (
                                        <>
                                            {upload?.trailer_show ? (
                                                <Box>
                                                    <CircularProgressWithLabel
                                                        color={colors.primary}
                                                        showValue={false}
                                                        value={upload.trailer_progress}
                                                    />
                                                </Box>
                                            ) : (
                                                <Button
                                                    onClick={() => handleSave()}
                                                    sx={{
                                                        '&:active, &:focus, &:hover': {
                                                            backgroundColor: '#252525',
                                                            color: 'white'
                                                        }
                                                    }}>
                                                    {t('Save')}
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default connect(mapStateToProps, null)(EditVideo);
